/**
 * =============================================================================
 * API CLIENT - Production-Ready API Integration Layer
 * =============================================================================
 * 
 * This module provides:
 * - Typed API client with automatic authentication
 * - Error handling for HTTP status codes (401, 403, 500)
 * - Optimistic UI update support
 * - Request/response interceptors
 * 
 * @module lib/api-client
 */

import type {
  ApiResponse,
  PaginatedResponse,
  Tenant,
  AuditLogEntry,
  User,
  UserRole,
  Transaction,
  ManualWithdrawalQueueEntry,
  BalanceResponse,
  LiveActivityResponse
} from '../../../shared/backend/types';

const AUTH_TOKEN_KEY = 'auth_token';

function getAuthTokenFromStorage(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

// =============================================================================
// Types
// =============================================================================

/**
 * API client configuration
 */
interface ApiClientConfig {
  baseUrl: string;
  getAuthToken: () => string | null;
  setAuthToken?: (token: string | null) => void;
  onUnauthorized?: () => void;
  onForbidden?: (code?: string, message?: string) => void;
  onServerError?: (message: string) => void;
  onNetworkError?: () => void;
}

/**
 * Request options for API calls
 */
interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean>;
}

/**
 * API error with structured details
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode: number,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Paginated request options
 */
type PaginationParams = Record<string, string | number | boolean>;

// =============================================================================
// API Client Class
// =============================================================================

/**
 * Production-ready API client with error handling and authentication
 */
export class ApiClient {
  private baseUrl: string;
  private getAuthToken: () => string | null;
  private setAuthToken: (token: string | null) => void;
  private onUnauthorized?: () => void;
  private onForbidden?: () => void;
  private onServerError?: (message: string) => void;
  private onNetworkError?: () => void;
  private isRefreshing = false;
  private refreshSubscribers: Array<(token: string) => void> = [];

  constructor(config: ApiClientConfig) {
    this.baseUrl = config.baseUrl.replace(/\/$/, '');
    this.getAuthToken = config.getAuthToken;
    this.setAuthToken = config.setAuthToken || (() => {});
    this.onUnauthorized = config.onUnauthorized;
    this.onForbidden = config.onForbidden;
    this.onServerError = config.onServerError;
    this.onNetworkError = config.onNetworkError;
  }

  setTokenSetter(setter: (token: string | null) => void): void {
    this.setAuthToken = setter;
  }

  private async refreshToken(): Promise<string | null> {
    if (this.isRefreshing) {
      return new Promise((resolve) => {
        this.refreshSubscribers.push(resolve);
      });
    }

    this.isRefreshing = true;

    try {
      const token = this.getAuthToken();
      if (!token) {
        return null;
      }

      const response = await fetch(`${this.baseUrl}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json() as ApiResponse<{ token: string }>;
      if (data.success && data.data?.token) {
        const newToken = data.data.token;
        this.setAuthToken(newToken);
        this.refreshSubscribers.forEach((cb) => cb(newToken));
        return newToken;
      }

      return null;
    } catch {
      return null;
    } finally {
      this.isRefreshing = false;
      this.refreshSubscribers = [];
    }
  }

  /**
   * Make authenticated API request with error handling
   */
  async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    // Build URL with query params
    let url = `${this.baseUrl}${endpoint}`;
    if (options.params) {
      const searchParams = new URLSearchParams();
      Object.entries(options.params).forEach(([key, value]) => {
        searchParams.append(key, String(value));
      });
      url += `?${searchParams.toString()}`;
    }

    // Build headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    // Merge options.headers into headers record
    if (options.headers) {
      if (Array.isArray(options.headers)) {
        options.headers.forEach(([key, value]) => {
          headers[key] = value;
        });
      } else {
        Object.entries(options.headers).forEach(([key, value]) => {
          headers[key] = value;
        });
      }
    }

    // Add auth token if available
    const token = this.getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Handle HTTP error status codes
      if (response.status === 401) {
        const newToken = await this.refreshToken();
        if (newToken) {
          headers['Authorization'] = `Bearer ${newToken}`;
          const retryResponse = await fetch(url, {
            ...options,
            headers,
          });
          if (retryResponse.ok) {
            const text = await retryResponse.text();
            return text ? JSON.parse(text) as T : ({} as T);
          }
        }
        this.onUnauthorized?.();
        throw new ApiError(
          'Authentication required. Please log in.',
          'UNAUTHORIZED',
          401
        );
      }

      if (response.status === 403) {
        this.onForbidden?.();
        throw new ApiError(
          'You do not have permission to perform this action.',
          'FORBIDDEN',
          403
        );
      }

      if (response.status >= 500) {
        const message = `Server error (${response.status}). Please try again later.`;
        this.onServerError?.(message);
        throw new ApiError(
          message,
          'SERVER_ERROR',
          response.status
        );
      }

      // Handle other error status codes
      if (!response.ok) {
        const errData = await response.json().catch(() => ({})) as { error?: { message?: string; code?: string; details?: unknown } };
        throw new ApiError(
          errData.error?.message || `Request failed with status ${response.status}`,
          errData.error?.code || 'REQUEST_FAILED',
          response.status,
          errData.error?.details as Record<string, unknown> | undefined
        );
      }

      // Handle empty responses
      const text = await response.text();
      if (!text) {
        return {} as T;
      }

      return JSON.parse(text) as T;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof ApiError) {
        throw error;
      }
      // Handle abort errors (timeout)
      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiError(
          'Request timed out. Please try again.',
          'TIMEOUT',
          0
        );
      }
      // Network errors, parse errors, etc.
      this.onNetworkError?.();
      throw new ApiError(
        'Network error. Please check your connection.',
        'NETWORK_ERROR',
        0
      );
    }
  }

  // =============================================================================
  // SaaS Owner API Endpoints
  // =============================================================================

  /**
   * Get platform-wide analytics
   */
  async getAnalytics(): Promise<ApiResponse<{
    total_players: number;
    active_tenants: number;
    total_revenue_cents: number;
    pending_withdrawals: number;
    recent_deposits_count: number;
  }>> {
    return this.request('/api/v1/saas/analytics');
  }

  /**
   * Get all tenants (casinos)
   */
  async getTenants(pagination?: PaginationParams): Promise<PaginatedResponse<Tenant>> {
    return this.request('/api/v1/saas/tenants', { params: pagination });
  }

  /**
   * Get single tenant by ID
   */
  async getTenant(id: string): Promise<ApiResponse<{ tenant: Tenant }>> {
    return this.request(`/api/v1/saas/tenants/${id}`);
  }

  /**
   * Create new tenant (casino)
   */
  async createTenant(data: {
    name: string;
    slug: string;
    domain?: string;
    casino_auto_withdraw_enabled?: boolean;
    house_edge_percentage?: number;
    withdrawal_fee_type?: 'flat' | 'percentage' | 'none';
    withdrawal_fee_amount?: number;
  }): Promise<ApiResponse<{ tenant: Tenant }>> {
    return this.request('/api/v1/saas/tenants', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update existing tenant
   */
  async updateTenant(id: string, data: Partial<{
    name: string;
    domain: string;
    is_active: boolean;
    casino_auto_withdraw_enabled: boolean;
    house_edge_percentage: number;
    withdrawal_fee_type: 'flat' | 'percentage' | 'none';
    withdrawal_fee_amount: number;
  }>): Promise<ApiResponse<{ tenant: Tenant }>> {
    return this.request(`/api/v1/saas/tenants/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Get platform configuration
   */
  async getPlatformConfig(): Promise<ApiResponse<{
    saas_auto_withdraw_enabled: boolean;
    default_revenue_share_percentage: number;
    withdrawal_fee_type: 'flat' | 'percentage' | 'none';
    withdrawal_fee_amount: number;
  }>> {
    return this.request('/api/v1/saas/config');
  }

  /**
   * Update platform configuration
   */
  async updatePlatformConfig(data: Partial<{
    saas_auto_withdraw_enabled: boolean;
    default_revenue_share_percentage: number;
    withdrawal_fee_type: 'flat' | 'percentage' | 'none';
    withdrawal_fee_amount: number;
  }>): Promise<ApiResponse<{ config: unknown }>> {
    return this.request('/api/v1/saas/config', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Get audit logs with pagination
   */
  async getAuditLogs(pagination?: PaginationParams): Promise<PaginatedResponse<AuditLogEntry>> {
    return this.request('/api/v1/saas/audit', { params: pagination });
  }

  /**
   * Get all users (SaaS owner view)
   */
  async getUsers(tenantId?: string, pagination?: PaginationParams): Promise<PaginatedResponse<User>> {
    const params: Record<string, string | number | boolean> = { ...pagination };
    if (tenantId) params['tenant_id'] = tenantId;
    return this.request('/api/v1/saas/users', { params });
  }

  // =============================================================================
  // Casino Manager API Endpoints
  // =============================================================================

  /**
   * Get casino-specific analytics
   */
  async getCasinoAnalytics(tenantId: string): Promise<ApiResponse<{
    total_players: number;
    active_players: number;
    daily_revenue_cents: number;
    pending_withdrawals: number;
    bet_count_today: number;
  }>> {
    return this.request(`/api/v1/casino/${tenantId}/analytics`);
  }

  /**
   * Get casino users (managers, agents, players)
   */
  async getCasinoUsers(tenantId: string, role?: UserRole, pagination?: PaginationParams): Promise<PaginatedResponse<User>> {
    const params: Record<string, string | number | boolean> = { ...pagination };
    if (role) params['role'] = role;
    return this.request(`/api/v1/casino/${tenantId}/users`, { params });
  }

  /**
   * Create user under casino
   */
  async createCasinoUser(tenantId: string, data: {
    email: string;
    password: string;
    username: string;
    role: UserRole;
    display_name?: string;
    parent_user_id?: string;
  }): Promise<ApiResponse<{ user: User }>> {
    return this.request(`/api/v1/casino/${tenantId}/users`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Get manual withdrawal queue for casino
   */
  async getWithdrawalQueue(tenantId: string, pagination?: PaginationParams): Promise<PaginatedResponse<ManualWithdrawalQueueEntry & { transaction: Transaction }>> {
    return this.request(`/api/v1/casino/${tenantId}/withdrawals/queue`, { params: pagination });
  }

  /**
   * Process manual withdrawal
   */
  async processWithdrawal(tenantId: string, queueId: string, action: 'approve' | 'reject', notes?: string): Promise<ApiResponse<{ queue_entry: ManualWithdrawalQueueEntry }>> {
    return this.request(`/api/v1/casino/${tenantId}/withdrawals/queue/${queueId}/process`, {
      method: 'POST',
      body: JSON.stringify({ action, processor_notes: notes }),
    });
  }

  // =============================================================================
  // Wallet API Endpoints
  // =============================================================================

  /**
   * Get user balance (cash + crypto)
   */
  async getBalance(userId: string): Promise<ApiResponse<BalanceResponse>> {
    return this.request(`/api/v1/wallet/balance/${userId}`);
  }

  /**
   * Get transaction history
   */
  async getTransactions(pagination?: PaginationParams): Promise<PaginatedResponse<Transaction>> {
    return this.request('/api/v1/wallet/transactions', { params: pagination });
  }

  /**
   * Request crypto withdrawal
   */
  async requestWithdrawal(data: {
    token_type: 'ETH' | 'USDC' | 'USDT';
    amount: string;
    destination_address: string;
  }): Promise<ApiResponse<{ transaction: Transaction }>> {
    return this.request('/api/v1/wallet/withdraw', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Get deposit address for crypto deposit
   */
  async getDepositAddress(tokenType: 'ETH' | 'USDC' | 'USDT'): Promise<ApiResponse<{ address: string; qr_code_url?: string }>> {
    return this.request(`/api/v1/wallet/deposit/address/${tokenType}`);
  }

  // =============================================================================
  // Engagement & Loyalty API Endpoints
  // =============================================================================

  /**
   * Get live activity feed (recent big wins)
   */
  async getLiveActivity(tenantId: string, pagination?: PaginationParams): Promise<ApiResponse<LiveActivityResponse>> {
    return this.request('/api/v1/casino/live-activity', { params: { tenant_id: tenantId, ...pagination } });
  }

  /**
   * Get VIP status for current user or target user
   */
  async getVipStatus(userId?: string): Promise<ApiResponse<{
    user_id: string;
    current_level: number;
    current_title: string;
    experience_points: number;
    next_level_xp: number;
    xp_progress_percent: number;
    benefits: Record<string, unknown>;
  }>> {
    const params: Record<string, string | number> = {};
    if (userId) params['user_id'] = userId;
    return this.request('/api/v1/casino/vip-status', { params });
  }

  /**
   * Get leaderboard
   */
  async getLeaderboard(tenantId: string, boardType: string, period: string, limit?: number): Promise<ApiResponse<{
    board_type: string;
    period: string;
    entries: {
      rank: number;
      user_id: string;
      display_name: string;
      value: number;
      avatar_url: string | null;
    }[];
    last_updated: string;
    cached: boolean;
  }>> {
    return this.request('/api/v1/casino/leaderboard', {
      params: { tenant_id: tenantId, type: boardType, period, ...(limit !== undefined ? { limit } : {}) }
    });
  }

  /**
   * Get current user profile
   */
  async getProfile(): Promise<ApiResponse<User>> {
    return this.request('/api/v1/users/profile');
  }

  /**
   * Update current user profile
   */
  async updateProfile(data: { display_name?: string; avatar_url?: string }): Promise<ApiResponse<User>> {
    return this.request('/api/v1/users/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Get recently earned rewards (bonus history)
   */
  async getBonusRewards(pagination?: PaginationParams): Promise<PaginatedResponse<{
    id: string;
    reward_type: string;
    xp_awarded: number | null;
    description: string;
    created_at: string;
  }>> {
    return this.request('/api/v1/casino/rewards/history', { params: pagination });
  }

  // =============================================================================
  // Helper Methods
  // =============================================================================

  /**
   * Check if the API is reachable (health check)
   * Returns detailed status instead of just boolean
   */
  async healthCheck(): Promise<{
    ok: boolean;
    errorType?: 'network' | 'server' | 'timeout' | 'unknown';
    errorMessage?: string;
  }> {
    try {
      await this.request('/api/v1/health');
      return { ok: true };
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.statusCode === 0) {
          return {
            ok: false,
            errorType: error.code === 'TIMEOUT' ? 'timeout' : 'network',
            errorMessage: error.message,
          };
        }
        if (error.statusCode >= 500) {
          return {
            ok: false,
            errorType: 'server',
            errorMessage: error.message,
          };
        }
      }
      return {
        ok: false,
        errorType: 'unknown',
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }
}

// =============================================================================
// Singleton Instance
// =============================================================================

export const apiClient = new ApiClient({
  baseUrl: import.meta.env.PUBLIC_API_BASE_URL || 'https://casino-saas-api.eu-nl.workers.dev',
  getAuthToken: getAuthTokenFromStorage,
  setAuthToken: (token: string | null) => {
    if (token) {
      localStorage.setItem(AUTH_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(AUTH_TOKEN_KEY);
    }
  },
  onUnauthorized: () => {
    console.warn('Unauthorized - redirect to login');
    window.location.href = '/login';
  },
  onForbidden: () => {
    console.warn('Forbidden - insufficient permissions');
  },
  onServerError: (message) => {
    console.error('Server error:', message);
  },
});

// =============================================================================
// Optimistic Update Helpers
// =============================================================================

/**
 * Result type for optimistic updates
 */
export type OptimisticResult<T> = {
  optimisticData: T;
  rollback: () => void;
};

/**
 * Generic optimistic update helper
 * 
 * @param currentData - Current state
 * @param optimisticUpdate - Function to apply optimistic change
 * @param apiCall - API call to execute
 * @param onSuccess - Success callback
 * @param onError - Error callback with rollback
 * 
 * @example
 * ```typescript
 * const { optimisticData, rollback } = await optimisticUpdate(
 *   tenants,
 *   (prev) => prev.map(t => t.id === id ? { ...t, status: 'inactive' } : t),
 *   () => apiClient.updateTenant(id, { is_active: false }),
 *   (data) => setTenants(data),
 *   (error) => { rollback(); showError(error.message); }
 * );
 * ```
 */
export async function optimisticUpdate<T>(
  currentData: T,
  optimisticUpdate: (prev: T) => T,
  apiCall: () => Promise<unknown>,
  onSuccess: (result: T) => void,
  onError: (error: ApiError, rollback: () => void) => void
): Promise<OptimisticResult<T>> {
  const optimisticData = optimisticUpdate(currentData);
  
  try {
    await apiCall();
    onSuccess(optimisticData);
    return { optimisticData, rollback: () => {} };
  } catch (error) {
    const rollback = () => onSuccess(currentData);
    if (error instanceof ApiError) {
      onError(error, rollback);
    } else {
      onError(new ApiError('Unknown error', 'UNKNOWN', 0), rollback);
    }
    return { optimisticData, rollback };
  }
}